import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { DOCUMENT_REQUEST_EXPIRED } from 'common/constants/documents';
import { DOCUMENTS_URL_PARAM, QUESTIONS_URL_PARAM } from 'common/constants/process-management';
import { useStore } from 'common/store';
import { DelegatedRequest, DelegationInfo } from 'common/types/delegation';
import { Question } from 'common/types/firm';
import { SecurePortalParams } from 'common/types/reactRouter';
import { MessageBox } from 'components';
import { TaskContextType } from 'context/TaskContext/types';
import ProcessManagementHeader from 'pages/ProcessManagement/components/ProcessManagementHeader/ProcessManagementHeader';
import ProcessManagementSidebar from 'pages/ProcessManagement/components/ProcessManagementSidebar/ProcessManagementSidebar';
import { RequestedFile } from 'pages/RequestDocuments/components/FolderIcon/types';
import { useDocuments, useTasks } from 'services/hooks';
import theme from 'theme';
import { Logger } from 'utilities';
import { SetPairedUrlsProps, TaskExtended, TaskLayoutProps } from './TaskLayout.props';
import { classes, StyledTaskContextProvider, StyledTaskLayout,StyledTaskLayoutContainer } from './TaskLayout.styles';



const TaskLayout = ({ children }: TaskLayoutProps) => {
  const { requestId, hmacToken, processType } = useParams<SecurePortalParams>();
  const { validateDocumentRequestToken } = useDocuments();
  const { validateQuestionnairesRequestToken } = useTasks();
  const [isValidToken, setIsValidToken] = useState(false);
  const [task, setTask] = useState<TaskExtended>({} as TaskExtended);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [companyDocuments, setCompanyDocuments] = useState([]);
  const headerMenuItemsInitialState = {
    left: [],
    right: [],
  };
  const [headerMenuItems, setHeaderMenuItems] = useState(headerMenuItemsInitialState);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [questionnaireUrl, setQuestionnaireUrl] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [{ user }] = useStore();
  const [requestedFiles, setRequestedFiles] = useState<RequestedFile[]>([]);
  const [requestedQuestions, setRequestedQuestions] = useState<Question[]>([]);
  const [pairedHmacTokenAndId, setPairedHmacTokenAndId] = useState({
    pairedHmacToken: '',
    pairedId: '',
  });
  const [delegationInfo, setDelegationInfo] = useState<DelegationInfo>({ is_delegated: false });
  const [delegatedRequests, setDelegatedRequests] = useState<DelegatedRequest[]>([]);

  const validateToken = useCallback(async () => {
    try {
      let response = {} as TaskExtended;
      if (processType === DOCUMENTS_URL_PARAM) {
        response = (await validateDocumentRequestToken(requestId, hmacToken)) as TaskExtended;
        setRequestedFiles(response.requested_files || []);
        setDelegationInfo(response.delegation_info || { is_delegated: false });
        setDelegatedRequests(response.delegated_requests || []);
        if (response.paired_questionnaire) {
          const { questionnaire_id: questionnaireId, questionnaire_token: questionnaireToken }
            = response.paired_questionnaire;
          const questionsResponse = await validateQuestionnairesRequestToken(questionnaireId, questionnaireToken);
          setRequestedQuestions(questionsResponse.questions);
          setPairedHmacTokenAndId({ pairedHmacToken: questionnaireToken, pairedId: questionnaireId });
        }
      } else if (processType === QUESTIONS_URL_PARAM) {
        response = await validateQuestionnairesRequestToken(requestId, hmacToken);
        setRequestedQuestions(response.questions);
        if (response.paired_document_request) {
          const { document_request_id: documentRequestId, document_token: documentToken }
            = response.paired_document_request;
          const documentsResponse = (await validateDocumentRequestToken(
            documentRequestId,
            documentToken
          )) as TaskExtended;
          setRequestedFiles(documentsResponse.requested_files || []);
          setPairedHmacTokenAndId({ pairedHmacToken: documentToken, pairedId: documentRequestId });
        }
      }

      if (response.task) {
        setTask(response);
        setIsValidToken(true);
      } else {
        setIsValidToken(false);
      }
    } catch (error) {
      Logger.error('Error validating token', error);
      setIsValidToken(false);
    } finally {
      setIsLoadingPage(false);
    }
  }, [hmacToken, processType, requestId, validateDocumentRequestToken, validateQuestionnairesRequestToken]);

  useEffect(() => {
    (async () => {
      try {
        await validateToken();
      } catch (err: any) {
        setIsValidToken(false);
        Logger.error('Error validating token', err);
        throw new Error(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processType, user]);

  function setPairedUrls({ taskParam, setDocumentUrlParam, setQuestionnaireUrlParam }: SetPairedUrlsProps) {
    if (taskParam.paired_document_request) {
      setDocumentUrlParam(
        `/${DOCUMENTS_URL_PARAM}/requests/${taskParam.paired_document_request.document_request_id}/${taskParam.paired_document_request.document_token}`
      );
    }
    if (taskParam.paired_questionnaire) {
      setQuestionnaireUrlParam(
        `/${QUESTIONS_URL_PARAM}/requests/${taskParam.paired_questionnaire.questionnaire_id}/${taskParam.paired_questionnaire.questionnaire_token}`
      );
    }
  }

  useEffect(() => {
    setPairedUrls({
      taskParam: task,
      setDocumentUrlParam: setDocumentUrl,
      setQuestionnaireUrlParam: setQuestionnaireUrl,
    });
  }, [task]);

  const taskProviderValue = useMemo(
    () =>
      ({
        task,
        headerMenuItems,
        setHeaderMenuItems,
        isMobile,
        companyDocuments,
        setCompanyDocuments,
        questionnaireUrl,
        documentUrl,
        validateToken,
        requestedFiles,
        requestedQuestions,
        pairedHmacTokenAndId,
        delegationInfo,
        delegatedRequests,
      }) as unknown as TaskContextType,
    [
      companyDocuments,
      delegatedRequests,
      delegationInfo,
      documentUrl,
      headerMenuItems,
      isMobile,
      pairedHmacTokenAndId,
      questionnaireUrl,
      requestedFiles,
      requestedQuestions,
      task,
      validateToken,
    ]
  );

  if (isLoadingPage) {
    return null;
  }

  if (!isValidToken && isEmpty(task)) {
    return (
      <div className={classes.messageBox}>
        <MessageBox
          title=""
          tagline={DOCUMENT_REQUEST_EXPIRED}
          type="error-inside"
          fullWidth={false}
          hasFrame={false}
          hasBgColor={false}
        />
      </div>
    );
  }

  return (
    <StyledTaskContextProvider value={taskProviderValue}>
      <Box display="grid" position="relative" top={0} right={0} zIndex={1000}
      >
        <ProcessManagementSidebar isOpen={false} />
        <ProcessManagementHeader />
        <StyledTaskLayout>
          <StyledTaskLayoutContainer maxWidth="xl">
            {children}
          </StyledTaskLayoutContainer>
        </StyledTaskLayout>
      </Box> 

    </StyledTaskContextProvider>
  );
};

export default TaskLayout;
